import React from 'react';
import { Input, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

const AuditLogList = ({ data, currentPage, itemsPerPage, totalRecords, totalPages, onPageChange, onItemsPerPageChange, sortBy, sortOrder, onSortChange }) => {

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        let hours = d.getHours();
        let minutes = d.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        return `${hours}:${minutes} ${ampm}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${months[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
    };

    const getMethodBadgeClass = (method) => {
        const m = (method || '').toUpperCase();
        if (m === 'GET') return 'badge-get';
        if (m === 'POST') return 'badge-post';
        if (m === 'PUT') return 'badge-put';
        if (m === 'DELETE') return 'badge-delete';
        return 'badge-get';
    };

    const getStatusClass = (code) => {
        if (!code) return '';
        const c = parseInt(code, 10);
        if (c >= 200 && c < 300) return 'status-success';
        if (c >= 300 && c < 400) return 'status-redirect';
        if (c >= 400 && c < 500) return 'status-client-err';
        if (c >= 500) return 'status-server-err';
        return '';
    };

    const formatMemory = (mem) => {
        if (!mem || !mem.delta) return 'N/A';
        const rss = mem.delta.rss ? mem.delta.rss.toFixed(2) : '0.00';
        const heap = mem.delta.heapUsed ? mem.delta.heapUsed.toFixed(2) : (mem.delta.heap ? mem.delta.heap.toFixed(2) : '0.00');
        return (
            <div className="memory-stat">
                <span className="memory-rss">{rss} MB RSS</span>
                <span className="memory-heap">{heap} MB Heap</span>
            </div>
        );
    };

    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalRecords);

    const handleSort = (field) => {
        if (sortBy === field) {
            onSortChange(field, sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            onSortChange(field, 'asc');
        }
    };

    const renderSortHeader = (label, field) => (
        <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort(field)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '11px', gap: '4px' }}>
                    <FaChevronUp color={sortBy === field && sortOrder === 'asc' ? '#000' : '#cbd5e1'} />
                    <FaChevronDown color={sortBy === field && sortOrder === 'desc' ? '#000' : '#cbd5e1'} />
                </span>
            </div>
        </th>
    );

    return (
        <div className="audit-list-card">
            <div className="audit-table-container">
                <table className="audit-table">
                    <thead>
                        <tr>
                            <th>User ID</th>
                            <th>Method</th>
                            {renderSortHeader('Path', 'path')}
                            {renderSortHeader('Feature', 'feature')}
                            <th>Status Code</th>
                            {renderSortHeader('Heap / RAM', 'memory')}
                            {renderSortHeader('Response Time', 'responseTime')}
                            {renderSortHeader('Date & Time', 'timestamp')}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.length > 0 ? (
                            data.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.userId || 'N/A'}</td>
                                    <td>
                                        <span className={`audit-badge ${getMethodBadgeClass(item.method)}`}>
                                            {item.method || 'N/A'}
                                        </span>
                                    </td>
                                    <td>{item.path || 'N/A'}</td>
                                    <td>{item.feature || 'N/A'}</td>
                                    <td>
                                        <span className={`audit-badge ${getStatusClass(item.statusCode)}`}>
                                            {item.statusCode || 'N/A'}
                                        </span>
                                    </td>
                                    <td>{formatMemory(item.memory)}</td>
                                    <td>{item.responseTime ? `${Number(item.responseTime).toFixed(2)} ms` : 'N/A'}</td>
                                    <td>
                                        <div className="time-container">
                                            <span className="time-am-pm">{formatTime(item.timestamp)}</span>
                                            <span className="time-date">{formatDate(item.timestamp)}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8">
                                    <div className="empty-state">
                                        No audit logs found for the selected filters.
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Pagination Footer */}
            {totalRecords > 0 && (
                <div className="pq-pagination-container d-flex justify-content-between align-items-center" style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">Showing {totalRecords > 0 ? indexOfFirstItem + 1 : 0} – {indexOfLastItem} of {totalRecords} entries. Per page: </span>
                        <Input type="select" value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))} className="pq-filter-input" style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </Input>
                    </div>

                    <Pagination size="sm" className="mb-0">
                        <PaginationItem className="pq-page-item" disabled={currentPage === 1}>
                            <PaginationLink className="pq-page-link" previous onClick={() => onPageChange(currentPage - 1)} />
                        </PaginationItem>

                        {[...Array(totalPages)].map((_, i) => {
                            if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <PaginationItem className="pq-page-item" active={i + 1 === currentPage} key={i}>
                                        <PaginationLink className="pq-page-link" onClick={() => onPageChange(i + 1)}>
                                            {i + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            if (i === totalPages - 2 && currentPage < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            return null;
                        })}

                        <PaginationItem className="pq-page-item" disabled={currentPage === totalPages || totalPages === 0}>
                            <PaginationLink className="pq-page-link" next onClick={() => onPageChange(currentPage + 1)} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default AuditLogList;
